import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import StockChartModal from "@/components/StockChartModal";
import { Star, Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, LineChart, LogIn, Bell, BellRing, Target, X, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AnalysisModal from "@/components/AnalysisModal";

const fmt = (n) => (n != null ? n.toLocaleString("id-ID") : "-");

function TargetManager({ item, onAdd, onDelete }) {
  const [price, setPrice] = useState("");
  const [dir, setDir] = useState("above");
  const targets = item.targets || [];
  return (
    <div className="mt-2 p-3 rounded-lg bg-[var(--surface-card)] border border-[var(--border)]" data-testid={`target-editor-${item.symbol}`}>
      {targets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {targets.map((t) => (
            <span key={t.id} data-testid={`target-chip-${item.symbol}-${t.id}`} className={`text-[11px] px-2 py-1 rounded-md border font-mono-data flex items-center gap-1.5 ${t.triggered ? "bg-[var(--gold)]/15 text-gold border-[var(--gold)]/30" : "bg-[var(--surface-base)] text-[var(--text-secondary)] border-[var(--border)]"}`}>
              {t.triggered ? <BellRing size={11} /> : <Target size={11} />}
              {t.direction === "below" ? "≤" : "≥"} {fmt(t.price)}{t.triggered ? " · tercapai" : ""}
              <button onClick={() => onDelete(item.symbol, t.id)} data-testid={`target-del-${item.symbol}-${t.id}`} className="hover:text-bear ml-0.5"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[var(--text-secondary)]">Alert saat harga</span>
        <div className="flex rounded-md overflow-hidden border border-[var(--border)]">
          <button onClick={() => setDir("above")} className={`px-2 py-1 text-xs font-semibold ${dir === "above" ? "bg-[var(--bull)] text-[#07090E]" : "bg-[var(--surface-base)] text-[var(--text-secondary)]"}`} data-testid={`dir-above-${item.symbol}`}>≥ TP</button>
          <button onClick={() => setDir("below")} className={`px-2 py-1 text-xs font-semibold ${dir === "below" ? "bg-[var(--bear)] text-white" : "bg-[var(--surface-base)] text-[var(--text-secondary)]"}`} data-testid={`dir-below-${item.symbol}`}>≤ SL</button>
        </div>
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="harga" data-testid={`target-input-${item.symbol}`} className="w-24 h-8 px-2 rounded-md bg-[var(--surface-base)] border border-[var(--border)] font-mono-data text-sm outline-none focus:border-[var(--accent)]/50" />
        <button onClick={() => { onAdd(item.symbol, price, dir); setPrice(""); }} data-testid={`target-add-${item.symbol}`} className="h-8 px-3 rounded-md bg-[var(--accent)] text-[#07090E] text-xs font-semibold flex items-center gap-1"><Plus size={13} /> Tambah Target</button>
        <span className="text-[10px] text-[var(--text-muted)]">maks 5 · TP=take profit, SL=stop loss</span>
      </div>
    </div>
  );
}

export default function WatchlistSection() {
  const { user, login } = useAuth();
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [chart, setChart] = useState(null);
  const [analyze, setAnalyze] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get("/watchlist");
      setItems(res.data.items || []);
    } catch (e) {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("watchlist-updated", h);
    return () => window.removeEventListener("watchlist-updated", h);
  }, [load]);

  const add = async () => {
    const sym = input.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!sym) return;
    setAdding(true);
    try {
      await api.post("/watchlist", { symbol: sym });
      setInput("");
      toast.success(`${sym} ditambahkan`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambahkan"); }
    finally { setAdding(false); }
  };

  const remove = async (sym) => {
    try { await api.delete(`/watchlist/${sym}`); setItems((it) => it.filter((i) => i.symbol !== sym)); }
    catch (e) { toast.error("Gagal menghapus"); }
  };

  const addTarget = async (sym, price, dir) => {
    if (!price || parseFloat(price) <= 0) { toast.error("Masukkan harga target"); return; }
    try {
      await api.post(`/watchlist/${sym}/targets`, { price: parseFloat(price), direction: dir });
      toast.success(`Target ${dir === "below" ? "SL" : "TP"} ${sym} ditambahkan`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambahkan target"); }
  };

  const delTarget = async (sym, id) => {
    try { await api.delete(`/watchlist/${sym}/targets/${id}`); await load(); }
    catch (e) { toast.error("Gagal menghapus target"); }
  };

  return (
    <section id="watchlist" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Pantauan Pribadi + Multi-Alert"
        title="Watchlist Saham Saya"
        desc="Simpan saham favorit, pantau harga live, dan pasang beberapa target harga (TP & SL) yang dikirim ke Telegram saat tercapai."
        icon={Star}
      />
      {!user ? (
        <div className="card p-8 text-center" data-testid="watchlist-login-prompt">
          <Star size={28} className="text-gold mx-auto mb-3" />
          <p className="text-[var(--text-secondary)] mb-4">Masuk untuk membuat watchlist pribadi Anda.</p>
          <button onClick={login} data-testid="watchlist-login-btn" className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]">
            <LogIn size={16} /> Masuk dengan Google
          </button>
        </div>
      ) : (
        <div className="card p-5">
          <div className="flex gap-2 mb-4">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Tambah kode saham (mis. BBCA)"
              data-testid="watchlist-input"
              className="flex-1 h-10 px-3 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] font-mono-data text-sm outline-none focus:border-[var(--accent)]/50 uppercase"
            />
            <button onClick={add} disabled={adding} data-testid="watchlist-add-btn" className="h-10 px-4 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center gap-1.5">
              <Plus size={16} /> Tambah
            </button>
            <button onClick={load} className="h-10 w-10 flex items-center justify-center rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-accent" data-testid="watchlist-refresh">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          {items.length === 0 ? (
            <div className="py-10 text-center text-[var(--text-muted)] text-sm" data-testid="watchlist-empty">Belum ada saham. Tambahkan kode di atas, klik "Watchlist" pada grafik, atau sinkronkan rekomendasi harian.</div>
          ) : (
            <div className="space-y-2">
              <AnimatePresence>
                {items.map((it) => {
                  const up = (it.change_pct ?? 0) >= 0;
                  const isEditing = editing === it.symbol;
                  const nTargets = (it.targets || []).length;
                  return (
                    <motion.div key={it.symbol} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -10 }} data-testid={`watchlist-item-${it.symbol}`} className="bg-[var(--surface-base)] rounded-lg p-3 border border-[var(--border-subtle)] hover:border-[var(--accent)]/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${up ? "bg-[var(--bull)]/12 text-bull" : "bg-[var(--bear)]/12 text-bear"}`}>{it.symbol.slice(0, 2)}</span>
                          <div>
                            <p className="font-heading font-bold">{it.symbol}</p>
                            <p className="text-[10px] text-[var(--text-muted)] font-mono-data">{nTargets > 0 ? `${nTargets} alert aktif` : "IDX"}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-mono-data font-semibold">{fmt(it.price)}</p>
                            {it.change_pct != null && (
                              <p className={`text-xs font-mono-data flex items-center gap-0.5 justify-end ${up ? "text-bull" : "text-bear"}`}>
                                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{up ? "+" : ""}{it.change_pct}%
                              </p>
                            )}
                          </div>
                          <button onClick={() => setEditing(isEditing ? null : it.symbol)} data-testid={`watchlist-alert-${it.symbol}`} className={`p-2 rounded-md bg-[var(--surface-hover)] ${nTargets > 0 ? "text-gold" : "text-[var(--text-secondary)]"} hover:text-gold relative`}>
                            <Bell size={15} />
                            {nTargets > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gold text-[#07090E] text-[9px] font-bold flex items-center justify-center">{nTargets}</span>}
                          </button>
                          <button onClick={() => setAnalyze(it.symbol)} data-testid={`watchlist-analyze-${it.symbol}`} className="p-2 rounded-md bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-accent"><Sparkles size={15} /></button>
                          <button onClick={() => setChart({ ticker: it.symbol })} data-testid={`watchlist-chart-${it.symbol}`} className="p-2 rounded-md bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-accent"><LineChart size={15} /></button>
                          <button onClick={() => remove(it.symbol)} data-testid={`watchlist-remove-${it.symbol}`} className="p-2 rounded-md bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-bear"><Trash2 size={15} /></button>
                        </div>
                      </div>
                      {isEditing && <TargetManager item={it} onAdd={addTarget} onDelete={delTarget} />}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
          <p className="text-xs text-[var(--text-muted)] mt-4">Alert dicek otomatis tiap 10 menit pada jam bursa (09.00–16.00 WIB). Ringkasan performa watchlist dikirim ke Telegram tiap sore (16.15 WIB).</p>
        </div>
      )}
      {chart && <StockChartModal ticker={chart.ticker} onClose={() => setChart(null)} />}
      {analyze && <AnalysisModal ticker={analyze} onClose={() => setAnalyze(null)} />}
    </section>
  );
}
